/**
 * Stock Availability Widget
 * Displays real-time stock status with visual indicators and add-to-cart functionality
 *
 * Features:
 * - Multi-state stock display: in-stock (green), low-stock (amber), out-of-stock (red), pre-order (blue), expected-soon (teal)
 * - Pre-order detection via [SKU]-Pre-Order-[DATE] tag pattern with formatted date display
 * - Visual progress bar with colour-coded quantity indication
 * - x-modal pattern for detailed stock information
 * - Add to cart functionality with success/error messaging
 * - Variant change handling with dynamic stock updates
 * - 11 inline SVG icons (box, calendar, check, check-circle, x-circle, truck, circle, info, arrow, warning, package)
 * - British English throughout (optimised, colour, categorised)
 * - Theme token styling
 *
 * @version 1.0.0
 */

(function(window) {
  'use strict';

  class StockAvailabilityWidget extends window.ProductWidgets.BaseWidget {
    constructor(manager, container, config) {
      super(manager, container, config);

      // Widget state
      this.currentVariant = null;
      this.stockStatus = 'unknown';
      this.stockQuantity = 0;
      this.preOrderDate = null;
      this.isPreOrder = false;
      this.maxDisplayQuantity = 50; // Max quantity for progress bar
      this.modalId = `stock-modal-${config.id}`;

      // Stock status configuration with British English - Black/White theme only
      this.statusConfig = {
        'in-stock': {
          label: 'In Stock',
          colour: 'var(--color-base-button)', // Use theme button color
          icon: 'check-circle',
          description: 'Available for immediate dispatch',
          badgeText: 'Available Now'
        },
        'low-stock': {
          label: 'Low Stock',
          colour: 'var(--color-drawer-text)', // Black/dark gray
          icon: 'warning',
          description: 'Limited quantity remaining - order soon',
          badgeText: 'Only {qty} left'
        },
        'out-of-stock': {
          label: 'Out of Stock',
          colour: 'var(--color-drawer-text)', // Black/dark gray (not red)
          icon: 'x-circle',
          description: 'Currently unavailable',
          badgeText: 'Sold Out'
        },
        'pre-order': {
          label: 'Pre-Order',
          colour: 'var(--color-drawer-text)', // Black/dark gray (not blue)
          icon: 'calendar',
          description: 'Reserve yours now - ships {date}',
          badgeText: 'Pre-Order'
        },
        'expected-soon': {
          label: 'Expected Soon',
          colour: 'var(--color-drawer-text)', // Black/dark gray (not teal)
          icon: 'truck',
          description: 'New stock arriving within 3 days',
          badgeText: 'Back Soon'
        }
      };
    }

    /**
     * Initialise widget - detect current variant and stock status
     */
    async init() {
      // Set up product data from config
      this.productData = {
        tags: (this.config.productTags && this.config.productTags.length > 0) ? this.config.productTags : [],
        variants: this.config.productVariants || [],
        selectedVariantId: this.config.selectedVariantId || null
      };

      // Find current variant
      this.currentVariant = this.findCurrentVariant();

      if (!this.currentVariant) {
        console.warn('[StockAvailabilityWidget] No variant found');
        return;
      }

      // Analyse stock status
      this.analyseStockStatus();
    }

    /**
     * Find current variant from product data
     * @returns {Object|null} Variant object
     */
    findCurrentVariant() {
      if (!this.productData.variants || this.productData.variants.length === 0) {
        return null;
      }

      // Try to find selected variant first
      if (this.productData.selectedVariantId) {
        const selected = this.productData.variants.find(
          v => v.id === this.productData.selectedVariantId
        );
        if (selected) return selected;
      }

      // Fallback to first available variant
      return this.productData.variants.find(v => v.available) || this.productData.variants[0];
    }

    /**
     * Analyse stock status based on variant data and tags
     */
    analyseStockStatus() {
      if (!this.currentVariant) {
        console.warn('[StockAvailabilityWidget] No current variant for stock analysis');
        this.stockStatus = 'unknown';
        return;
      }

      // Check for pre-order tag pattern: [SKU]-Pre-Order-[DATE]
      const preOrderTag = this.findPreOrderTag();
      if (preOrderTag) {
        this.isPreOrder = true;
        this.preOrderDate = this.extractPreOrderDate(preOrderTag);
        this.stockStatus = 'pre-order';
        this.stockQuantity = this.currentVariant.inventory_quantity || 0;
        console.log('[StockAvailabilityWidget] Pre-order detected:', this.preOrderDate, 'Qty:', this.stockQuantity);
        return;
      }

      // Check inventory quantity
      const qty = this.currentVariant.inventory_quantity || 0;
      this.stockQuantity = qty;

      if (qty > 10) {
        this.stockStatus = 'in-stock';
      } else if (qty >= 1 && qty <= 10) {
        this.stockStatus = 'low-stock';
      } else if (qty === 0 && this.currentVariant.inventory_policy === 'continue') {
        // Allow backorder - expected soon
        this.stockStatus = 'expected-soon';
      } else {
        this.stockStatus = 'out-of-stock';
      }

      console.log('[StockAvailabilityWidget] Stock status:', this.stockStatus, 'Qty:', this.stockQuantity);
    }

    /**
     * Find pre-order tag matching pattern [SKU]-Pre-Order-[DATE]
     * @returns {string|null} Pre-order tag or null
     */
    findPreOrderTag() {
      if (!this.currentVariant.sku || !this.productData.tags) {
        return null;
      }

      const sku = this.currentVariant.sku;
      const pattern = `${sku}-Pre-Order-`;

      for (const tag of this.productData.tags) {
        if (tag.includes(pattern)) {
          return tag;
        }
      }

      return null;
    }

    /**
     * Extract and format date from pre-order tag
     * @param {string} tag - Pre-order tag (e.g., "ABC123-Pre-Order-15th December")
     * @returns {string} Formatted date
     */
    extractPreOrderDate(tag) {
      if (!tag) return '';

      const parts = tag.split('-Pre-Order-');
      if (parts.length < 2) return '';

      return parts[1].trim();
    }

    /**
     * Calculate progress bar percentage
     * @returns {number} Percentage (0-100)
     */
    calculateProgressPercentage() {
      if (this.stockQuantity <= 0) return 0;
      if (this.stockQuantity >= this.maxDisplayQuantity) return 100;

      return Math.round((this.stockQuantity / this.maxDisplayQuantity) * 100);
    }

    /**
     * Get status configuration
     * @returns {Object} Status config object
     */
    getStatusConfig() {
      return this.statusConfig[this.stockStatus] || this.statusConfig['out-of-stock'];
    }

    /**
     * Render widget to container
     */
    render() {
      // Only show widget if we have valid stock data
      if (!this.currentVariant) {
        this.hide();
        return;
      }

      // Build card + modal inline (x-modal pattern)
      const html = `
        ${this.buildCardHTML()}
        ${this.buildModalHTML()}
      `;

      // Use setContent() to properly manage skeleton loading
      this.setContent(html);

      // Attach event listeners
      this.attachEventListeners();

      // Listen for variant changes
      this.setupVariantChangeListener();

      this.emit('stock-availability:rendered');
    }

    /**
     * Build widget card HTML
     */
    buildCardHTML() {
      const status = this.getStatusConfig();
      const progressPercent = this.calculateProgressPercentage();

      return `
        <div class="pw-widget pw-stock-availability-widget" style="width: 100%;">
          <div class="pw-widget--card pw-widget--card-clickable" style="padding: 16px; cursor: pointer;" title="Click to view stock details">

            <!-- Header with Status Icon -->
            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 12px;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: rgb(var(--color-base-button) / 0.1); border-radius: var(--rounded-button);">
                ${this.getIconSVG(status.icon, 'var(--color-base-button)')}
              </div>
              <div style="flex: 1;">
                <h3 class="pw-text-lg pw-font-bold" style="margin: 0;">Stock Availability</h3>
                <p class="pw-text-sm" style="margin: 0; color: rgb(var(--color-drawer-text) / 0.7);">
                  ${status.description.replace('{date}', this.preOrderDate || '')}
                </p>
              </div>
            </div>

            <!-- Status Badge -->
            <div style="margin-bottom: 16px;">
              <div style="display: inline-flex; align-items: center; gap: 8px; padding: 8px 16px; background: rgb(var(--color-base-button) / 0.1); border: 2px solid rgb(var(--color-base-button) / 0.3); border-radius: var(--rounded-button);">
                ${this.getCircleIconSVG('var(--color-base-button)')}
                <span style="font-weight: 700; font-size: 0.95rem; color: rgb(var(--color-base-button));">
                  ${status.badgeText.replace('{qty}', this.stockQuantity)}
                  ${this.isPreOrder && this.preOrderDate ? ` - Ships ${this.preOrderDate}` : ''}
                </span>
              </div>
            </div>

            ${this.buildProgressBarHTML(progressPercent)}

            <!-- Quick Info -->
            <div style="display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px;">
              ${this.buildQuickInfoItems()}
            </div>

            <!-- Learn More Button -->
            <div style="display: flex; justify-content: flex-end;">
              <button
                type="button"
                class="pw-learn-more"
                aria-controls="${this.modalId}"
                aria-expanded="false"
              >
                <span>Learn more</span>
                <svg class="pw-learn-more__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>

          </div>
        </div>
      `;
    }

    /**
     * Build progress bar HTML
     * @param {number} progressPercent - Progress percentage (0-100)
     * @returns {string} Progress bar HTML
     */
    buildProgressBarHTML(progressPercent) {
      // Show progress bar for in-stock, low-stock, and out-of-stock
      if (this.stockStatus === 'in-stock' || this.stockStatus === 'low-stock' || this.stockStatus === 'out-of-stock') {
        const barColor = this.stockStatus === 'out-of-stock'
          ? 'rgb(var(--color-drawer-text) / 0.3)'
          : 'rgb(var(--color-base-button))';  // FIX: Wrap in rgb()

        const labelColor = this.stockStatus === 'out-of-stock'
          ? 'var(--color-drawer-text)'
          : 'var(--color-base-button)';

        return `
          <!-- Progress Bar -->
          <div style="margin-bottom: 16px;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
              <span class="pw-text-sm" style="color: rgb(var(--color-drawer-text) / 0.7);">
                ${this.stockQuantity} ${this.stockQuantity === 1 ? 'unit' : 'units'} ${this.stockStatus === 'out-of-stock' ? 'in stock' : 'available'}
              </span>
              <span class="pw-text-sm pw-font-bold" style="color: rgb(${labelColor});">
                ${progressPercent}%
              </span>
            </div>
            <div style="width: 100%; height: 8px; background: rgb(var(--color-drawer-text) / 0.1); border-radius: 999px; overflow: hidden;">
              <div class="pw-progress-bar-fill" style="width: ${progressPercent}%; height: 100%; background: ${barColor}; border-radius: 999px; transition: width 0.3s ease;">&nbsp;</div>
            </div>
          </div>
        `;
      }

      // Don't show progress bar for pre-order or expected-soon
      return '';
    }

    /**
     * Build quick info items based on status
     */
    buildQuickInfoItems() {
      const items = [];

      switch (this.stockStatus) {
        case 'in-stock':
        case 'low-stock':
          items.push(`
            <div style="display: flex; align-items: start; gap: 8px;">
              ${this.getCheckIconSVG()}
              <span class="pw-text-sm" style="color: rgb(var(--color-drawer-text) / 0.9);">
                Dispatches within 1-2 working days
              </span>
            </div>
          `);
          items.push(`
            <div style="display: flex; align-items: start; gap: 8px;">
              ${this.getCheckIconSVG()}
              <span class="pw-text-sm" style="color: rgb(var(--color-drawer-text) / 0.9);">
                Free UK delivery (3-5 days) - Next Day available
              </span>
            </div>
          `);
          break;

        case 'pre-order':
          items.push(`
            <div style="display: flex; align-items: start; gap: 8px;">
              ${this.getCalendarIconSVG('18px', 'var(--color-base-button)')}
              <span class="pw-text-sm" style="color: rgb(var(--color-drawer-text) / 0.9);">
                Expected dispatch: ${this.preOrderDate || 'TBC'}
              </span>
            </div>
          `);
          items.push(`
            <div style="display: flex; align-items: start; gap: 8px;">
              ${this.getCheckIconSVG()}
              <span class="pw-text-sm" style="color: rgb(var(--color-drawer-text) / 0.9);">
                Secure your order now - pay today
              </span>
            </div>
          `);
          break;

        case 'expected-soon':
          items.push(`
            <div style="display: flex; align-items: start; gap: 8px;">
              ${this.getTruckIconSVG('18px', '20, 184, 166')}
              <span class="pw-text-sm" style="color: rgb(var(--color-drawer-text) / 0.9);">
                New stock arriving within 3 days
              </span>
            </div>
          `);
          items.push(`
            <div style="display: flex; align-items: start; gap: 8px;">
              ${this.getCheckIconSVG()}
              <span class="pw-text-sm" style="color: rgb(var(--color-drawer-text) / 0.9);">
                Reserve yours now - ships when available
              </span>
            </div>
          `);
          break;

        case 'out-of-stock':
          items.push(`
            <div style="display: flex; align-items: start; gap: 8px;">
              ${this.getWarningIconSVG('18px', 'var(--color-drawer-text)')}
              <span class="pw-text-sm" style="color: rgb(var(--color-drawer-text) / 0.9);">
                Currently unavailable
              </span>
            </div>
          `);
          items.push(`
            <div style="display: flex; align-items: start; gap: 8px;">
              ${this.getInfoIconSVG('18px')}
              <span class="pw-text-sm" style="color: rgb(var(--color-drawer-text) / 0.9);">
                Check similar products or contact us
              </span>
            </div>
          `);
          break;
      }

      return items.join('');
    }

    /**
     * Build modal HTML using x-modal pattern
     */
    buildModalHTML() {
      const status = this.getStatusConfig();

      return `
        <x-modal id="${this.modalId}" class="x-modal drawer z-30 fixed bottom-0 left-0 h-full w-full pointer-events-none" role="dialog" aria-modal="true" aria-label="Stock Details" hidden>
          <overlay-element class="overlay fixed-modal invisible opacity-0 fixed bottom-0 left-0 w-full h-screen pointer-events-none" aria-controls="${this.modalId}" aria-expanded="false"></overlay-element>
          <div class="drawer__inner z-10 absolute top-0 flex flex-col w-full h-full overflow-hidden">
            <gesture-element class="drawer__header flex items-center justify-between opacity-0 invisible relative">
              <span class="drawer__title heading font-bold title-md">Stock Availability</span>
              <button class="button button--secondary button--close drawer__close hidden sm:flex items-center justify-center" type="button" is="hover-button" aria-controls="${this.modalId}" aria-expanded="false" aria-label="Close">
                <span class="btn-fill" data-fill></span>
                <span class="btn-text">
                  <svg class="icon icon-close icon-sm" viewBox="0 0 20 20" stroke="currentColor" fill="none">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 15L15 5M5 5L15 15"></path>
                  </svg>
                </span>
              </button>
            </gesture-element>
            <div class="drawer__content opacity-0 invisible flex flex-col h-full grow shrink">
              <div class="drawer__scrollable relative flex flex-col gap-5 md:gap-7 grow shrink">

                <!-- Hero Section -->
                <div class="text-center">
                  <div style="display: inline-flex; align-items: center; justify-content: center; width: 4rem; height: 4rem; background: var(--color-base-button); border-radius: 50%; margin-bottom: 1rem;">
                    ${this.getIconSVG(status.icon, 'var(--color-base-button-text)', '2rem')}
                  </div>
                  <h3 class="heading font-bold text-xl md:text-2xl" style="margin-bottom: 0.75rem; color: rgb(var(--color-drawer-text));">
                    ${status.label}
                  </h3>
                  <p class="text-base" style="max-width: 500px; margin: 0 auto; color: rgb(var(--color-drawer-text) / 0.8);">
                    ${status.description.replace('{date}', this.preOrderDate || '')}
                  </p>
                </div>

                <!-- Stock Summary -->
                ${this.buildStockSummaryHTML(status)}

                <!-- Delivery Information -->
                ${this.buildDeliveryInfoHTML()}

                <!-- Stock FAQ -->
                ${this.buildStockFAQHTML()}

                <!-- Add to Cart (if available) -->
                ${this.currentVariant.available ? this.buildAddToCartHTML() : ''}

              </div>
            </div>
          </div>
        </x-modal>
      `;
    }

    /**
     * Build stock summary section for modal
     */
    buildStockSummaryHTML(status) {
      const progressPercent = this.calculateProgressPercentage();

      return `
        <div style="padding: 1.5rem; background: rgb(var(--color-drawer-text) / 0.03); border-radius: var(--rounded-card); border: 1px solid rgb(var(--color-drawer-text) / 0.08);">
          <h4 class="font-bold text-base" style="margin-bottom: 1rem; color: rgb(var(--color-drawer-text));">Current Stock Status:</h4>

          <!-- Status Badge -->
          <div style="margin-bottom: 1.25rem; text-align: center;">
            <div style="display: inline-flex; align-items: center; gap: 10px; padding: 12px 24px; background: rgb(var(--color-base-button) / 0.1); border: 2px solid rgb(var(--color-base-button) / 0.3); border-radius: var(--rounded-button);">
              ${this.getCircleIconSVG('var(--color-base-button)', '24px')}
              <span style="font-weight: 700; font-size: 1.125rem; color: rgb(var(--color-base-button));">
                ${status.badgeText.replace('{qty}', this.stockQuantity)}
              </span>
            </div>
          </div>

          ${(this.stockStatus === 'in-stock' || this.stockStatus === 'low-stock') ? `
            <!-- Progress Bar -->
            <div style="margin-bottom: 1rem;">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                <span class="text-sm" style="color: rgb(var(--color-drawer-text) / 0.7);">
                  ${this.stockQuantity} ${this.stockQuantity === 1 ? 'unit' : 'units'} in stock
                </span>
                <span class="text-sm font-bold" style="color: rgb(var(--color-base-button));">
                  ${progressPercent}%
                </span>
              </div>
              <div style="width: 100%; height: 12px; background: rgb(var(--color-drawer-text) / 0.1); border-radius: 999px; overflow: hidden;">
                <div class="pw-progress-bar-fill" style="width: ${progressPercent}%; height: 100%; background: rgb(var(--color-base-button)); border-radius: 999px; transition: width 0.3s ease;">&nbsp;</div>
              </div>
            </div>
          ` : ''}

          ${this.isPreOrder && this.preOrderDate ? `
            <!-- Pre-Order Date -->
            <div style="padding: 1rem; background: rgb(var(--color-drawer-text) / 0.03); border-radius: var(--rounded-card); border: 1px solid rgb(var(--color-drawer-text) / 0.1);">
              <div style="display: flex; align-items: center; gap: 12px;">
                ${this.getCalendarIconSVG('24px', 'var(--color-base-button)')}
                <div>
                  <div class="text-sm font-bold" style="color: rgb(var(--color-drawer-text)); margin-bottom: 4px;">Expected Dispatch Date:</div>
                  <div class="text-base" style="color: rgb(var(--color-base-button)); font-weight: 600;">${this.preOrderDate}</div>
                </div>
              </div>
            </div>
          ` : ''}
        </div>
      `;
    }

    /**
     * Build delivery information section
     */
    buildDeliveryInfoHTML() {
      return `
        <div style="padding: 1.5rem; background: rgb(var(--color-drawer-text) / 0.03); border-radius: var(--rounded-card); border: 1px solid rgb(var(--color-drawer-text) / 0.08);">
          <h4 class="font-bold text-base" style="margin-bottom: 1rem; color: rgb(var(--color-drawer-text));">Delivery Information:</h4>

          <div style="display: flex; flex-direction: column; gap: 1rem;">
            <div style="display: flex; align-items: start; gap: 12px;">
              ${this.getTruckIconSVG('20px', 'var(--color-base-button)')}
              <div style="flex: 1;">
                <div class="text-sm font-bold" style="color: rgb(var(--color-drawer-text)); margin-bottom: 4px;">Standard Delivery</div>
                <div class="text-sm" style="color: rgb(var(--color-drawer-text) / 0.85);">
                  ${this.stockStatus === 'in-stock' || this.stockStatus === 'low-stock' ? 'Dispatches within 1-2 working days' : this.stockStatus === 'pre-order' ? `Dispatches ${this.preOrderDate}` : 'Currently unavailable'}
                </div>
              </div>
            </div>

            <div style="display: flex; align-items: start; gap: 12px;">
              ${this.getPackageIconSVG('20px', 'var(--color-base-button)')}
              <div style="flex: 1;">
                <div class="text-sm font-bold" style="color: rgb(var(--color-drawer-text)); margin-bottom: 4px;">Free UK Delivery</div>
                <div class="text-sm" style="color: rgb(var(--color-drawer-text) / 0.85);">
                  3-5 days on all orders - Next Day available
                </div>
              </div>
            </div>

            <div style="display: flex; align-items: start; gap: 12px;">
              ${this.getBoxIconSVG('20px', 'var(--color-base-button)')}
              <div style="flex: 1;">
                <div class="text-sm font-bold" style="color: rgb(var(--color-drawer-text)); margin-bottom: 4px;">Secure Packaging</div>
                <div class="text-sm" style="color: rgb(var(--color-drawer-text) / 0.85);">
                  All items carefully packed and protected
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    /**
     * Build stock FAQ section
     */
    buildStockFAQHTML() {
      return `
        <div style="padding: 1.5rem; background: rgb(var(--color-drawer-text) / 0.03); border-radius: var(--rounded-card); border: 1px solid rgb(var(--color-drawer-text) / 0.08);">
          <h4 class="font-bold text-base" style="margin-bottom: 1rem; color: rgb(var(--color-drawer-text));">Frequently Asked Questions:</h4>

          <div style="display: flex; flex-direction: column; gap: 1.25rem;">

            ${this.stockStatus === 'low-stock' ? `
              <div>
                <div class="text-sm font-bold" style="color: rgb(var(--color-base-button)); margin-bottom: 0.5rem; display: flex; align-items: center; gap: 6px;">
                  ${this.getInfoIconSVG('16px')}
                  Why is stock limited?
                </div>
                <p class="text-sm" style="margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
                  This is a popular item and stock is running low. We recommend ordering soon to avoid disappointment.
                </p>
              </div>
            ` : ''}

            ${this.isPreOrder ? `
              <div>
                <div class="text-sm font-bold" style="color: rgb(var(--color-base-button)); margin-bottom: 0.5rem; display: flex; align-items: center; gap: 6px;">
                  ${this.getInfoIconSVG('16px')}
                  What is a pre-order?
                </div>
                <p class="text-sm" style="margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
                  Pre-ordering allows you to secure this item before it arrives in stock. You pay now and we'll dispatch on ${this.preOrderDate}.
                </p>
              </div>
            ` : ''}

            <div>
              <div class="text-sm font-bold" style="color: rgb(var(--color-base-button)); margin-bottom: 0.5rem; display: flex; align-items: center; gap: 6px;">
                ${this.getInfoIconSVG('16px')}
                Can I track my order?
              </div>
              <p class="text-sm" style="margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
                Yes! You'll receive tracking information via email once your order is dispatched.
              </p>
            </div>

            <div>
              <div class="text-sm font-bold" style="color: rgb(var(--color-base-button)); margin-bottom: 0.5rem; display: flex; align-items: center; gap: 6px;">
                ${this.getInfoIconSVG('16px')}
                What if stock runs out?
              </div>
              <p class="text-sm" style="margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
                ${this.stockStatus === 'low-stock' ? 'Stock is limited - order now to guarantee availability.' : 'We regularly restock popular items. Contact us to check when this item will be available again.'}
              </p>
            </div>
          </div>
        </div>
      `;
    }

    /**
     * Build add to cart section for modal
     */
    buildAddToCartHTML() {
      return `
        <div style="padding: 1.5rem; background: rgb(var(--color-base-button) / 0.1); border-radius: var(--rounded-card); border: 2px solid rgb(var(--color-base-button) / 0.3);">
          <h4 class="font-bold text-lg" style="margin-bottom: 1rem; color: rgb(var(--color-drawer-text));">
            ${this.isPreOrder ? 'Pre-Order Now' : 'Add to Cart'}
          </h4>

          <!-- Note: Variant and quantity selection handled by main product form -->

          <!-- Add to Cart Button -->
          <button
            id="add-to-cart-btn-${this.modalId}"
            type="button"
            style="width: 100%; background: rgb(var(--color-base-button)); color: rgb(var(--color-base-button-text)); border: none; padding: 1rem; border-radius: var(--rounded-button); font-size: 1.125rem; font-weight: 700; cursor: pointer; transition: all 0.2s ease;"
            onmouseover="this.style.opacity='0.9'"
            onmouseout="this.style.opacity='1'">
            ${this.isPreOrder ? 'Pre-Order Now' : 'Add to Cart'}
          </button>

          <!-- Status Message Container -->
          <div id="cart-status-${this.modalId}" style="margin-top: 1rem; text-align: center; display: none;"></div>
        </div>
      `;
    }

    /**
     * Attach event listeners
     */
    attachEventListeners() {
      // Add to cart button - use new handleAddToCart method
      const addButton = document.getElementById(`add-to-cart-btn-${this.modalId}`);
      if (addButton) {
        addButton.addEventListener('click', (e) => this.handleAddToCart(e));
      }

      // Make entire card clickable to open modal (like tier progress widget)
      const card = this.container.querySelector('.pw-widget--card');
      if (card) {
        card.addEventListener('click', (e) => this.handleCardClick(e));
      }
    }

    /**
     * Handle card click - open modal (like tier progress widget)
     */
    handleCardClick(event) {
      // Don't trigger if clicking on a button or link
      if (event.target.closest('button') || event.target.closest('a')) {
        return;
      }

      // Find the Learn More button and trigger it
      const learnMoreButton = this.container.querySelector('.pw-learn-more');
      if (learnMoreButton) {
        learnMoreButton.click();
      }
    }

    /**
     * Setup variant change listener
     */
    setupVariantChangeListener() {
      // Listen for variant change events from product form
      document.addEventListener('variant:change', (event) => {
        if (event.detail && event.detail.variant) {
          this.handleVariantChange(event.detail.variant);
        }
      });

      // Also listen to custom event from theme
      this.on('product:variant-change', (data) => {
        if (data.variant) {
          this.handleVariantChange(data.variant);
        }
      });
    }

    /**
     * Handle variant change
     * @param {Object} variant - New variant data
     */
    handleVariantChange(variant) {
      this.currentVariant = variant;
      this.analyseStockStatus();

      // Re-render widget
      this.render();

      this.emit('stock-availability:variant-changed', {
        variant: variant,
        status: this.stockStatus,
        quantity: this.stockQuantity
      });
    }

    /**
     * Handle add to cart button click
     * Works like tier progress widget - uses ProductLinkingManager and opens bundle drawer
     */
    async handleAddToCart(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      // Ensure ProductLinkingManager is loaded
      if (!window.ProductLinkingManager) {
        console.error('[StockAvailabilityWidget] ProductLinkingManager not loaded');
        alert('Unable to prepare bundle. Please refresh the page and try again.');
        return;
      }

      // CHECK PARENT STOCK FIRST - Don't allow if product is out of stock
      const parentStockCheck = await this.checkParentStockViaAPI();

      if (!parentStockCheck.available) {
        console.warn('[StockAvailabilityWidget] Product out of stock:', parentStockCheck.message);
        alert('This product is currently out of stock. Please check back later or contact us for availability.');
        return;
      }

      // Get product data from config
      const productId = this.config.productId;
      const productHandle = this.config.productHandle;
      const productVariantId = this.currentVariant ? this.currentVariant.id : null;

      if (!productId || !productVariantId) {
        console.error('[StockAvailabilityWidget] Missing product data:', { productId, productVariantId });
        alert('Unable to add to cart. Product information is missing.');
        return;
      }

      // Ensure parent product is in cart first using ProductLinkingManager
      const linkingManager = new ProductLinkingManager({
        productId: productId,
        productHandle: productHandle,
        productVariantId: productVariantId,

        // Success callback - parent is ready
        onReady: (productLinkId, cartItem) => {
          // Create standard package for bundle drawer
          const standardPackage = {
            key: 'standard-package',
            name: 'Standard Package',
            tier: 'standard',
            items: [
              {
                id: productVariantId,
                title: this.config.productTitle || 'Main Product',
                price: cartItem.price,
                quantity: 1,
                variant_id: productVariantId,
                product_id: productId,
                isPrimaryProduct: true
              }
            ],
            total: cartItem.price,
            savings: 0,
            discount: 0,
            productId: productId,
            productVariantId: productVariantId,
            productLinkId: productLinkId
          };

          // Store package globally for bundle drawer
          window.preSelectedTierPackage = standardPackage;

          // Open bundle drawer
          const bundleDrawer = document.querySelector('drawer-element#BundleDrawer');
          if (bundleDrawer) {
            bundleDrawer.show();

            // Emit event
            this.emit('stock-availability:bundle-opened', {
              package: 'standard',
              productId: productId,
              productLinkId: productLinkId
            });
          } else {
            console.error('[StockAvailabilityWidget] Bundle drawer not found');
            alert('Unable to open bundle configurator. Please try adding from the main product page.');
          }
        },

        // Cancel callback
        onCancel: () => {
          // User cancelled - silent
        },

        // Error callback
        onError: (error) => {
          console.error('[StockAvailabilityWidget] Error ensuring parent in cart:', error);
          alert('Failed to prepare your order. Please try again.');
        }
      });

      // Trigger the parent product check and cart addition
      linkingManager.ensureParentInCart();
    }

    /**
     * Check parent stock by fetching fresh data from Shopify API
     * Bypasses Liquid caching issues
     * @returns {Promise<{available: boolean, message: string}>}
     */
    async checkParentStockViaAPI() {
      if (!this.config.productHandle) {
        console.warn('[StockAvailabilityWidget] No product handle available');
        return {
          available: true,
          message: 'No product handle available'
        };
      }

      try {
        const response = await fetch(`/products/${this.config.productHandle}.js`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const productData = await response.json();

        return this.checkVariantsStock(productData.variants, productData.title);

      } catch (error) {
        console.error('[StockAvailabilityWidget] Error fetching product data:', error);
        return {
          available: true, // Don't block on error
          message: 'Error fetching product data'
        };
      }
    }

    /**
     * Check stock across variants
     * @param {Array} variants - Variant array
     * @param {string} title - Product title
     * @returns {{available: boolean, message: string}}
     */
    checkVariantsStock(variants, title) {
      if (!variants || variants.length === 0) {
        return {
          available: true,
          message: 'No variants to check'
        };
      }

      // Check if ANY variant has stock available
      const hasStockInAnyVariant = variants.some(variant => {
        // Check if variant is available
        if (variant.available === true) {
          return true;
        }

        // Check inventory policy - if 'continue', can sell when out of stock
        if (variant.inventory_policy === 'continue') {
          return true;
        }

        // Check if inventory management is disabled
        if (!variant.inventory_management || variant.inventory_management === null) {
          return true;
        }

        // Check if variant has inventory quantity > 0
        if (variant.inventory_quantity && variant.inventory_quantity > 0) {
          return true;
        }

        return false;
      });

      if (hasStockInAnyVariant) {
        return {
          available: true,
          message: `Product "${title}" has stock available`
        };
      } else {
        return {
          available: false,
          message: `Product "${title}" is out of stock`
        };
      }
    }

    /**
     * Cleanup
     */
    destroy() {
      // Remove modal from DOM
      const modal = document.getElementById(this.modalId);
      if (modal) {
        modal.remove();
      }

      super.destroy();
    }

    // ============================================================
    // SVG ICONS (11 total)
    // ============================================================

    /**
     * Get icon SVG by name
     */
    getIconSVG(iconName, colourRGB = 'var(--color-base-button)', size = '20px') {
      switch (iconName) {
        case 'check-circle':
          return this.getCheckCircleIconSVG(colourRGB, size);
        case 'x-circle':
          return this.getXCircleIconSVG(colourRGB, size);
        case 'warning':
          return this.getWarningIconSVG(size, colourRGB);
        case 'calendar':
          return this.getCalendarIconSVG(size, colourRGB);
        case 'truck':
          return this.getTruckIconSVG(size, colourRGB);
        default:
          return this.getInfoIconSVG(size);
      }
    }

    /**
     * Box icon (packaging)
     */
    getBoxIconSVG(size = '20px', colourRGB = 'var(--color-base-button)') {
      return `
        <svg style="width: ${size}; height: ${size}; flex-shrink: 0; color: rgb(${colourRGB});" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
        </svg>
      `;
    }

    /**
     * Calendar icon (dates)
     */
    getCalendarIconSVG(size = '20px', colourRGB = 'var(--color-base-button)') {
      return `
        <svg style="width: ${size}; height: ${size}; flex-shrink: 0; color: rgb(${colourRGB});" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
        </svg>
      `;
    }

    /**
     * Check icon (success) - with circle like warranty widget
     */
    getCheckIconSVG() {
      return `
        <svg style="flex-shrink: 0; width: 20px; height: 20px; color: rgb(var(--color-base-button));" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      `;
    }

    /**
     * Check circle icon (success with circle)
     */
    getCheckCircleIconSVG(colourRGB = 'var(--color-base-button)', size = '20px') {
      return `
        <svg style="width: ${size}; height: ${size}; flex-shrink: 0; color: rgb(${colourRGB});" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      `;
    }

    /**
     * X circle icon (error)
     */
    getXCircleIconSVG(colourRGB = 'var(--color-drawer-text)', size = '20px') {
      return `
        <svg style="width: ${size}; height: ${size}; flex-shrink: 0; color: rgb(${colourRGB});" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      `;
    }

    /**
     * Truck icon (delivery)
     */
    getTruckIconSVG(size = '20px', colourRGB = 'var(--color-base-button)') {
      return `
        <svg style="width: ${size}; height: ${size}; flex-shrink: 0; color: rgb(${colourRGB});" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"/>
        </svg>
      `;
    }

    /**
     * Circle icon (status indicator)
     */
    getCircleIconSVG(colourRGB = 'var(--color-base-button)', size = '16px') {
      return `
        <svg style="width: ${size}; height: ${size}; flex-shrink: 0; color: rgb(${colourRGB});" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <circle cx="10" cy="10" r="8"/>
        </svg>
      `;
    }

    /**
     * Info icon (information)
     */
    getInfoIconSVG(size = '20px') {
      return `
        <svg style="width: ${size}; height: ${size}; flex-shrink: 0; color: rgb(var(--color-base-button));" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      `;
    }

    /**
     * Arrow right icon (CTA)
     */
    getArrowRightIconSVG() {
      return `
        <svg style="width: 18px; height: 18px; color: rgb(var(--color-drawer-text));" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
        </svg>
      `;
    }

    /**
     * Warning icon (alerts)
     */
    getWarningIconSVG(size = '20px', colourRGB = 'var(--color-base-button)') {
      return `
        <svg style="width: ${size}; height: ${size}; flex-shrink: 0; color: rgb(${colourRGB});" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
        </svg>
      `;
    }

    /**
     * Package icon (parcel)
     */
    getPackageIconSVG(size = '20px', colourRGB = 'var(--color-base-button)') {
      return `
        <svg style="width: ${size}; height: ${size}; flex-shrink: 0; color: rgb(${colourRGB});" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/>
        </svg>
      `;
    }
  }

  // Register widget
  if (!window.ProductWidgets || !window.ProductWidgets.widgets) {
    window.ProductWidgets = window.ProductWidgets || {};
    window.ProductWidgets.widgets = window.ProductWidgets.widgets || {};
  }

  window.ProductWidgets.widgets.StockAvailabilityWidget = StockAvailabilityWidget;

})(window);
